<!-- The email editor's content (in Email.vue's dark layer): PRIMARY EMAIL, SECONDARY EMAILS,
     ASSISTANT (another person's account, linked: useAssistant; its heading, with a "?", always:
     the address, or its Add row). One keyed list, so a promoted address slides to its new place.
     A star makes a secondary primary; a chevron replaces the primary (#new-email) or the
     assistant (#new-email&assistant); trash marks an address or the assistant for removal
     (struck through; clicking it again, or the address, keeps it) -->

<template>
  <div ref="rootRef" class="email-editor" :aria-busy="isBusy">
    <TransitionGroup name="email-list" tag="div" class="list">
      <template v-for="item in items" :key="item.key">
        <h3 v-if="item.kind === 'heading'" class="section" :class="{groupStart: item.isGroupStart}">
          {{ item.text }}<AppHelpPopup
            v-if="item.help"
            id="assistantHelp"
            class="help"
            label="About your assistant"
            bounds-selector=".edit-layer">
            <span class="helpParagraph">{{ item.help }}</span>
          </AppHelpPopup>
        </h3>
        <button
          v-else-if="item.kind === 'add'"
          class="add"
          :class="{groupStart: item.isGroupStart}"
          type="button"
          :disabled="isBusy"
          @click="openAdd(item.purpose)">
          <AppModalProfileEmailIcon name="plus" />
          {{ item.text }}
        </button>
        <div
          v-else
          class="email-row"
          :class="{pending: item.isPending, primary: item.kind === 'primary'}">
          <span
            v-if="item.kind === 'primary' && !hasOthers"
            class="icon star filled"
            role="img"
            aria-label="Primary email">
            <AppModalProfileEmailIcon name="star-filled" />
          </span>
          <button
            v-else-if="item.kind === 'primary'"
            class="icon star filled"
            type="button"
            :aria-label="`${item.email} is your primary email`"
            title="Your primary email. To change it, choose another email's star.">
            <AppModalProfileEmailIcon name="star-filled" />
          </button>
          <span v-else-if="item.kind === 'assistant'" class="icon" aria-hidden="true" />
          <button
            v-else
            class="icon star"
            type="button"
            :disabled="isBusy || item.isPending"
            :aria-label="`Make ${item.email} your primary email`"
            title="Make primary"
            @click="promote(item.id)">
            <AppModalProfileEmailIcon name="star" />
          </button>
          <button
            v-if="item.isPending"
            class="address restore-text"
            type="button"
            :aria-label="`Keep ${item.email}`"
            @click="togglePendingRemoval(item.id)">
            {{ item.email }}
          </button>
          <span v-else class="address">{{ item.email }}</span>
          <button
            v-if="item.kind === 'assistant' && !item.isPending"
            class="icon"
            type="button"
            :disabled="isBusy"
            aria-label="Change your assistant"
            title="Change assistant"
            @click="openAdd('assistant')">
            <AppModalProfileEmailIcon name="chevron" />
          </button>
          <button
            v-if="item.kind === 'primary'"
            class="icon"
            type="button"
            :disabled="isBusy"
            aria-label="Replace your primary email with a new one"
            title="Replace with a new email"
            @click="openAdd('replace')">
            <AppModalProfileEmailIcon name="chevron" />
          </button>
          <button
            v-else
            class="icon"
            type="button"
            :disabled="isBusy"
            :aria-label="item.isPending ? `Keep ${item.email}` : `Remove ${item.email}`"
            :title="item.isPending ? 'Keep' : 'Remove'"
            @click="togglePendingRemoval(item.id)">
            <AppModalProfileEmailIcon :name="item.isPending ? 'restore' : 'trash'" />
          </button>
        </div>
      </template>
    </TransitionGroup>
    <div v-if="access" class="access">
      <h3 class="section groupStart">Account Access</h3>
      <p class="accessText">{{ accessText }}</p>
      <button class="add" type="button" :disabled="isBusy || isEnding" @click="endTheAccess">
        End Access
      </button>
      <p class="accessNote">Ending it also signs you out on your other devices.</p>
    </div>
    <p v-if="errorMessage" class="error" role="alert">{{ errorMessage }}</p>
  </div>
</template>

<script setup>
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import AppHelpPopup from "@/components/App/HelpPopup";
import AppModalProfileEmailIcon from "@/components/App/Modal/Profile/EmailIcon";
import useUserSafe from "@/composables/useUserSafe";
import {MAX_SECONDARY_EMAILS, getEmailKinds} from "@/composables/emailKinds";
import {useEmailEdit} from "@/composables/useEmailEdit";
import {ASSISTANT_ROW_ID, endAccess, useAssistant} from "@/composables/useAssistant";

const ADD_HASH = {replace: "#new-email", secondary: "#new-email&secondary",
  assistant: "#new-email&assistant"};
// the same words as the Add Assistant dialog (EmailChange/Confirm.vue)
const ASSISTANT_HELP = "Your assistant gets a copy of the emails we send you. They can't sign "
  + "in to your account.";

const router = useRouter();
const {user} = useUserSafe();
const {
  pendingRemovalIds, isBusy, errorMessage, promotingId, togglePendingRemoval, promoteEmail,
} = useEmailEdit();

function getRow(address, kind) {
  const isPending = pendingRemovalIds.value.includes(address.id);
  const {id, emailAddress: email} = address;
  return {key: id, id, kind, email, isPending};
}

// a promotion under way shows its result at once (useEmailEdit)
const kinds = computed(() => {
  return getEmailKinds(user.value, promotingId.value || user.value?.primaryEmailAddressId);
});
const hasOthers = computed(() => kinds.value.secondaries.length > 0);

const SECONDARY_HEADING = {key: "h-secondary", kind: "heading", text: "Secondary Emails"};

function getSecondaryItems({secondaries}) {
  const add = {key: "add-secondary", kind: "add", purpose: "secondary",
    text: "Add Secondary Email"};
  if (!secondaries.length) return [add];
  const rows = secondaries.map((address) => getRow(address, "secondary"));
  if (secondaries.length >= MAX_SECONDARY_EMAILS) return [SECONDARY_HEADING, ...rows];
  return [SECONDARY_HEADING, ...rows, add];
}

// shown once known (Email.vue loads it as Settings opens, before the editor); the group starts
// a little further down: the secondaries' Add row above it sits closer than an email row would
const {assistant, access, isAssistantLoaded} = useAssistant();
const isEnding = ref(false);

// someone given access to this account (from a payment for it, approved by this account): who,
// and until when it can be opened (a visit already open lasts until it's ended)
const accessText = computed(() => {
  const {name, email, opensUntil} = access.value;
  const who = name === email ? email : `${name} (${email})`;
  if (Date.now() > opensUntil) return `${who} can no longer open your account.`;
  const options = {day: "numeric", month: "long", year: "numeric"};
  const date = new Date(opensUntil).toLocaleDateString(undefined, options);
  return `${who} can open your account until ${date}.`;
});

async function endTheAccess() {
  isEnding.value = true;
  const result = await endAccess();
  isEnding.value = false;
  if (!result.ok) errorMessage.value = result.message;
}
const ASSISTANT_HEADING = {key: "h-assistant", kind: "heading", text: "Assistant",
  help: ASSISTANT_HELP, isGroupStart: true};

function getAssistantItems() {
  if (!isAssistantLoaded.value) return [];
  const add = {key: "add-assistant", kind: "add", purpose: "assistant", text: "Add Assistant"};
  if (!assistant.value) return [ASSISTANT_HEADING, add];
  const isPending = pendingRemovalIds.value.includes(ASSISTANT_ROW_ID);
  const row = {key: "assistant", id: ASSISTANT_ROW_ID, kind: "assistant", isPending,
    email: assistant.value.email};
  return [ASSISTANT_HEADING, row];
}

const items = computed(() => [
  {key: "h-primary", kind: "heading", text: "Primary Email"},
  ...(kinds.value.primary ? [getRow(kinds.value.primary, "primary")] : []),
  ...getSecondaryItems(kinds.value),
  ...getAssistantItems(),
]);

function openAdd(purpose) {
  router.push({hash: ADD_HASH[purpose]});
}

function promote(id) {
  promoteEmail(user.value, id);
}

// Email.vue focuses the first control on opening
const rootRef = ref(null);
const getFirstControl = () => rootRef.value?.querySelector("button:not([disabled])");
defineExpose({focus: () => getFirstControl()?.focus()});
</script>

<style scoped>
/* spacing: 4px within a group, 8px between rows, 12px before a section (18px before the
   assistant's), 6px before the footer */
.email-editor {
  --row-height: 32px;
  --muted: rgba(255, 255, 255, 0.55);
  flex: 1;
  min-width: 0;
  padding: 9px 0 2px; /* + the footer's 4px: 6px */
  text-align: left;
  font-size: 14px;
  font-weight: 400;
}
.list {
  display: flex;
  flex-direction: column;
}
/* small light capitals (as Settings' former hint), light on dark */
.section {
  margin: 12px 0 4px;
  font-size: 9px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--muted);
}
.section.groupStart {
  margin-top: 18px;
}
/* HelpPopup's "?" after the heading, a little larger than its 9px capitals; its bubble's text
   not in the heading's capitals */
.section > .help {
  font-size: 11px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
}
/* its bubble: lighter than the dark layer, with a faint edge (HelpPopup's dark mode); kept
   inside the layer (bounds-selector), which clips what overflows it */
.help :deep(.bubble) {
  --bubble-fill: #4a4c50;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.22), 0 6px 18px rgba(0, 0, 0, 0.6);
}
.accessText,
.accessNote {
  margin: 0 0 6px;
  overflow-wrap: anywhere;
}
.accessNote {
  margin-top: 4px;
  font-size: 12px;
  color: var(--muted);
}
.section:first-child {
  margin-top: 0;
}
.email-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: var(--row-height);
}
.address {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.email-row.primary .address {
  color: var(--brand-color); /* selected, as its star */
}
.email-row.pending .address {
  text-decoration: line-through;
  color: var(--muted);
}
/* icon and text buttons: global.css styles reset (CLAUDE.md § Vue conventions) */
.icon,
.restore-text,
.add {
  appearance: none;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  min-width: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
.restore-text {
  text-align: left;
}
/* a 24px box centred on the 16px icon (FORMS.md § 17: the ring centred on what it marks) */
.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 24px;
  height: 24px;
}
.star.filled {
  color: var(--brand-color); /* the year's brand colour: selected, as elsewhere */
  cursor: default;
}
.icon:disabled {
  color: var(--muted);
  cursor: default;
}
/* a full-width row, a little lower than an email's: a place for another address, not a call to
   action */
.add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 28px;
  margin: 8px 0 0;
  padding: 0 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: transparent;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}
.add :deep(svg) {
  width: 12px; /* a small plus: the label leads */
  height: 12px;
}
.add:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  color: white;
}
.icon:hover:not(:disabled) {
  color: rgba(255, 255, 255, 0.75);
}
.error {
  margin: 6px 0 0;
  font-size: 13px;
  color: #ffb4a8;
}
/* a light ring on the dark layer (the dialog's own ring is dark green: index.vue) */
.email-editor :is(button):focus-visible {
  outline: 2px solid white !important;
  outline-offset: 0;
}
.email-list-move {
  transition: transform 220ms ease-out;
}
.email-list-enter-active {
  transition: opacity 180ms ease-out;
}
.email-list-enter-from {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .email-list-move,
  .email-list-enter-active {
    transition: none;
  }
}
/* the primary address's star does nothing on a click: no hover (DESIGN.md § 15) */
.icon.star.filled:hover {
  color: var(--brand-color);
}
.restore-text:hover {
  color: white;
}
</style>
